import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar, CartesianGrid, Cell as RCell, ComposedChart, LabelList, Line, ReferenceArea, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { getBsView } from '../../api/financials_bs';
import { getPlView } from '../../api/financials_pl';
import './WorkingCapitalSynthesis.css';

/* FONDS DE ROULEMENT › SYNTHÈSE (prototype validé par Olivier, 2026-09-30).
   Quatre blocs : quatre chiffres ; la trésorerie et le besoin en fonds de
   roulement (BFR), en composition ou net ; la table des postes ; le pont de la
   variation depuis la clôture. Tout se LIT du bilan prévisionnel (soldes de
   fin de mois) : aucun calcul métier ici, seulement l'addition des postes
   (créances + stocks − fournisseurs − acomptes − TVA due). */

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
/* Les postes, dans l'ordre de lecture : ce qu'on détient, puis ce qu'on doit.
   `sign` dit leur sens dans le BFR ; `tab` l'onglet qui les règle. */
const POSTES = [
  { key: 'ar', std: 'ar_clients', label: 'Créances clients', sign: 1, tab: 'clients', color: 'var(--color-accent)' },
  { key: 'stock', std: 'inventory', label: 'Stocks', sign: 1, tab: 'stock', color: 'var(--color-accent-soft)' },
  { key: 'ap', std: 'ap_suppliers', label: 'Fournisseurs', sign: -1, tab: 'suppliers', color: 'var(--wcs-owed-1)' },
  { key: 'adv', std: 'customer_advances', label: 'Acomptes clients', sign: -1, tab: 'advances', color: 'var(--wcs-owed-2)' },
  { key: 'vat', std: 'vat_due', role: 'vat', label: 'TVA due', sign: -1, tab: 'vat', color: 'var(--wcs-owed-3)' },
];

export default function WorkingCapitalSynthesis({ activeTenant, year, unit = 'kchf', onOpenTab }) {
  const p = useP();
  const tid = activeTenant?.id;
  const k = unit === 'chf' ? 1 : 1000;
  const [bs, setBs] = useState(null);
  const [pl, setPl] = useState(null);
  const [mode, setMode] = usePersistentValue(`fin_wc_syn_mode_${tid}`, 'comp');

  useEffect(() => {
    if (!tid || !year) return undefined;
    let alive = true;
    Promise.all([getBsView(tid, year, { flatForecast: true }), getPlView(tid, year)])
      .then(([b, l]) => { if (alive) { setBs(b && !b.error ? b.data : null); setPl(l && !l.error ? l.data : null); } })
      .catch(() => { if (alive) { setBs(null); setPl(null); } });
    return () => { alive = false; };
  }, [tid, year]);

  const data = useMemo(() => {
    if (!bs) return null;
    const closed = num(bs.closed_count);
    const lines = bs.lines || [];
    const rows = bs.rows || [];
    const series = (pred) => {
      const line = lines.find(pred);
      const r = line && rows.find((x) => x.code === line.id);
      return MOIS.map((_, i) => num(r?.months?.[String(i + 1)]) / k);
    };
    const s = {};
    POSTES.forEach((po) => {
      s[po.key] = series((l) => l.std_key === po.std || (po.role && l.line_role === po.role));
    });
    s.cash = series((l) => l.std_key === 'liquidity');
    const bfr = MOIS.map((_, i) => POSTES.reduce((a, po) => a + po.sign * s[po.key][i], 0));
    const rev = (pl?.rows || []).find((x) => x.code === 'sec:revenue');
    const ca = MOIS.map((_, i) => num(rev?.months?.[String(i + 1)]) / k);
    // Le BFR en jours du chiffre d'affaires des trois derniers mois.
    const days = (i) => {
      const t = ca.slice(Math.max(0, i - 2), i + 1).reduce((a, b) => a + b, 0);
      return t ? Math.round((bfr[i] / t) * 90) : null;
    };
    return { closed, s, bfr, days };
  }, [bs, pl, k]);

  if (!tid) return null;
  if (!data) return <p className="wcs-empty">{p('Chargement…')}</p>;
  const { closed, s, bfr, days } = data;
  const c0 = Math.max(0, closed - 1);              // index de la clôture (0 = janvier)
  const fmt = (v) => (v == null ? '—' : Math.round(v).toLocaleString('de-CH'));
  const sgn = (v) => `${v > 0 ? '+' : ''}${fmt(v)}`;
  const uLbl = unit === 'chf' ? 'CHF' : 'kCHF';
  const openIdx = MOIS.map((_, i) => i).filter((i) => i >= closed);
  const low = openIdx.length ? openIdx.reduce((a, i) => (s.cash[i] < s.cash[a] ? i : a), openIdx[0]) : 11;

  /* 1. Quatre chiffres. */
  const kpis = [
    [p('Besoin en fonds de roulement'), fmt(bfr[11]), `${p('fin')} ${year} · ${sgn(bfr[11] - bfr[c0])} ${p('depuis la clôture')}`],
    [p('En jours de chiffre d\'affaires'), days(11) == null ? '—' : `${days(11)} j`, `${p('à la clôture')} : ${days(c0) == null ? '—' : `${days(c0)} j`}`],
    [p('Trésorerie'), fmt(s.cash[11]), `${p('fin')} ${year}`],
    [p('Trésorerie au plus bas'), fmt(s.cash[low]), `${MOIS[low]} ${year}`],
  ];

  /* 2. Le graphique : barres empilées (au-dessus de zéro ce qu'on détient,
     dessous ce qu'on doit), lignes du BFR net et de la trésorerie. */
  const chart = MOIS.map((m, i) => {
    const o = { m, bfr: bfr[i], cash: s.cash[i] };
    POSTES.forEach((po) => { o[po.key] = po.sign * s[po.key][i]; });
    return o;
  });

  /* 4. Le pont : du BFR de clôture à celui de décembre, poste par poste. */
  const steps = [{ n: p('BFR clôture'), base: true, v: bfr[c0] },
    ...POSTES.map((po) => ({ n: p(po.label), v: po.sign * (s[po.key][11] - s[po.key][c0]) })),
    { n: `${p('BFR')} ${MOIS[11]}`, base: true, v: bfr[11] }];
  let run = 0;
  const bridge = steps.map((st) => {
    if (st.base) { run = st.v; return { n: st.n, range: [0, st.v], v: st.v, kind: 'base' }; }
    const from = run; run += st.v;
    return { n: st.n, range: [from, run], v: st.v, kind: st.v > 0 ? 'up' : 'dn' };
  });
  const bColor = { base: 'var(--color-navy)', up: 'var(--color-delta-alert)', dn: 'var(--color-delta-favorable)' };

  const axisProps = { tick: { fontSize: 12, fill: 'var(--color-text-muted)' }, tickLine: false };
  /* Le libellé d'une barre du pont se coupe sur deux lignes à l'espace : « Acomptes clients » ne tenait pas sous sa barre. */
const WrapTick = ({ x, y, payload }) => {
  const mots = String(payload.value).split(' ');
  const milieu = mots.length > 1 ? Math.ceil(mots.length / 2) : 1;
  const lignes = mots.length > 1 ? [mots.slice(0, milieu).join(' '), mots.slice(milieu).join(' ')] : mots;
  return (
    <text x={x} y={y + 12} textAnchor="middle" fontSize={11} fill="var(--color-text-muted)">
      {lignes.map((l, i) => <tspan key={i} x={x} dy={i ? 13 : 0}>{l}</tspan>)}
    </text>
  );
};
const tip = { formatter: (v) => (Array.isArray(v) ? fmt(v[1] - v[0]) : fmt(v)), contentStyle: { fontSize: 12, borderRadius: 8 } };

  return (
    <div className="wcs">
      <div className="wcs-card wcs-kpis">
        {kpis.map((x) => (
          <div key={x[0]} className="wcs-kpi">
            <div className="wcs-kpi__l">{x[0]}</div>
            <div className="wcs-kpi__v">{x[1]}</div>
            <div className="wcs-kpi__c">{x[2]}</div>
          </div>
        ))}
      </div>

      <div className="wcs-card">
        <div className="wcs-card__h">
          <h2>{p('Trésorerie et besoin en fonds de roulement')}</h2>
          <span className="wcs-sub">{year} · {uLbl} · {p('soldes de fin de mois')}</span>
          <span className="wcs-acts">
            <SegmentedToggle compact value={mode} onChange={setMode}
              options={[{ value: 'comp', label: p('Composition') }, { value: 'net', label: p('BFR net') }]} />
          </span>
        </div>
        <div className="wcs-chart">
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chart} stackOffset="sign" margin={{ top: 16, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="var(--color-border-inner)" vertical={false} />
              {closed < 12 && <ReferenceArea x1={MOIS[closed]} x2={MOIS[11]} fill="var(--color-accent-tint)" fillOpacity={0.6} />}
              <XAxis dataKey="m" {...axisProps} axisLine={{ stroke: 'var(--color-border)' }} />
              <YAxis {...axisProps} axisLine={false} width={56} tickFormatter={fmt} />
              <ReferenceLine y={0} stroke="var(--color-text-muted)" />
              <Tooltip {...tip} />
              {mode === 'comp'
                ? POSTES.map((po) => <Bar key={po.key} dataKey={po.key} name={p(po.label)} stackId="w" fill={po.color} maxBarSize={36} />)
                : <Bar dataKey="bfr" name={p('BFR')} fill="var(--color-accent-soft)" maxBarSize={36} />}
              <Line dataKey="bfr" name={p('BFR net')} stroke="var(--color-navy)" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} isAnimationActive={false} />
              <Line dataKey="cash" name={p('Trésorerie')} stroke="var(--color-sage)" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="wcs-legend">
          {mode === 'comp' && POSTES.map((po) => (
            <span key={po.key}><i style={{ background: po.color }} />{p(po.label)}{po.sign < 0 ? ` (${p('en négatif')})` : ''}</span>
          ))}
          <span><i className="wcs-line" style={{ background: 'var(--color-navy)' }} />{p('BFR net')}</span>
          <span><i className="wcs-line" style={{ background: 'var(--color-sage)' }} />{p('Trésorerie')}</span>
        </div>
      </div>

      <div className="wcs-duo">
        <div className="wcs-card">
          <div className="wcs-card__h"><h2>{p('Postes du fonds de roulement')}</h2><span className="wcs-sub">{uLbl}</span></div>
          <div className="lk-hscroll">
            <table className="wcs-tbl">
              <thead>
                <tr>
                  <th />
                  <th>{p('Clôture')}</th>
                  {openIdx.map((i) => <th key={i} className={i === closed ? 'wcs-edge' : ''}>{MOIS[i]}</th>)}
                  <th className="wcs-edge">{p('Variation')}</th>
                </tr>
              </thead>
              <tbody>
                {POSTES.map((po) => (
                  <tr key={po.key} className="wcs-click" onClick={() => onOpenTab && onOpenTab(po.tab)} title={p('Ouvrir le poste')}>
                    <td><span className="wcs-dot" style={{ background: po.color }} />{po.sign < 0 ? '− ' : ''}{p(po.label)}</td>
                    <td>{fmt(s[po.key][c0])}</td>
                    {openIdx.map((i) => <td key={i} className={i === closed ? 'wcs-edge' : ''}>{fmt(s[po.key][i])}</td>)}
                    <td className="wcs-edge">{sgn(po.sign * (s[po.key][11] - s[po.key][c0]))}</td>
                  </tr>
                ))}
                <tr className="wcs-strong">
                  <td>= {p('Besoin en fonds de roulement')}</td>
                  <td>{fmt(bfr[c0])}</td>
                  {openIdx.map((i) => <td key={i} className={i === closed ? 'wcs-edge' : ''}>{fmt(bfr[i])}</td>)}
                  <td className="wcs-edge">{sgn(bfr[11] - bfr[c0])}</td>
                </tr>
                <tr className="wcs-memo">
                  <td>{p('en jours de chiffre d\'affaires')}</td>
                  <td>{days(c0) == null ? '—' : `${days(c0)} j`}</td>
                  {openIdx.map((i) => <td key={i} className={i === closed ? 'wcs-edge' : ''}>{days(i) == null ? '—' : `${days(i)} j`}</td>)}
                  <td className="wcs-edge">{days(11) == null || days(c0) == null ? '—' : `${days(11) - days(c0) > 0 ? '+' : ''}${days(11) - days(c0)} j`}</td>
                </tr>
                <tr className="wcs-sub-row">
                  <td>{p('Trésorerie')}</td>
                  <td>{fmt(s.cash[c0])}</td>
                  {openIdx.map((i) => <td key={i} className={i === closed ? 'wcs-edge' : ''}>{fmt(s.cash[i])}</td>)}
                  <td className="wcs-edge">{sgn(s.cash[11] - s.cash[c0])}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="wcs-card">
          <div className="wcs-card__h"><h2>{p('D\'où vient la variation')}</h2><span className="wcs-sub">{p('clôture')} → 31.12.{year}</span></div>
          <div className="wcs-chart">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={bridge} margin={{ top: 22, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="var(--color-border-inner)" vertical={false} />
                <XAxis dataKey="n" {...axisProps} axisLine={{ stroke: 'var(--color-border)' }} interval={0} height={44} tick={<WrapTick />} />
                <YAxis {...axisProps} axisLine={false} width={56} tickFormatter={fmt} />
                <ReferenceLine y={0} stroke="var(--color-text-muted)" />
                <Tooltip {...tip} />
                <Bar dataKey="range" name={p('Variation')} maxBarSize={44} isAnimationActive={false}>
                  {bridge.map((b) => <RCell key={b.n} fill={bColor[b.kind]} />)}
                  <LabelList dataKey="v" position="top" formatter={(v) => fmt(v)} style={{ fontSize: 11, fill: 'var(--color-text)' }} />
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="wcs-legend">
            <span><i style={{ background: 'var(--color-delta-alert)' }} />{p('consomme de la trésorerie')}</span>
            <span><i style={{ background: 'var(--color-delta-favorable)' }} />{p('en apporte')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
